import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  const [gameId, setGameId] = useState(null);
  const [currentGuess, setCurrentGuess] = useState('');
  const [humanGuesses, setHumanGuesses] = useState([]);
  const [aiResultsColors, setAiResultsColors] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [gameRevealed, setGameRevealed] = useState(false);
  const [revealedData, setRevealedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [animatingRow, setAnimatingRow] = useState(-1);
  const [letterStates, setLetterStates] = useState({});
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Show a brief scroll hint when results first appear on mobile
  useEffect(() => {
    if (isMobile && gameRevealed) {
      setShowScrollHint(true);
      const t = setTimeout(() => setShowScrollHint(false), 2500);
      return () => clearTimeout(t);
    }
  }, [isMobile, gameRevealed]);

  // QWERTY keyboard layout
  const keyboardRows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE']
  ];

  // Update letter states based on guess results (memoized)
  const updateLetterStates = useCallback((word, result) => {
    setLetterStates(prevStates => {
      const newStates = { ...prevStates };
      for (let i = 0; i < word.length; i++) {
        const letter = word[i];
        const color = result[i];
        // Only update if we don't have a better state already
        // Priority: green > yellow > gray
        if (!newStates[letter] ||
            (newStates[letter] === 'gray' && color === 'yellow') ||
            (newStates[letter] === 'yellow' && color === 'green') ||
            color === 'green') {
          newStates[letter] = color;
        }
      }
      return newStates;
    });
  }, []);

  const startNewGame = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/api/start-game`, {
        method: 'POST',
      });
      const data = await response.json();
      
      setGameId(data.game_id);
      setHumanGuesses([]);
      setAiResultsColors([]);
      setGameComplete(false);
      setGameRevealed(false);
      setRevealedData(null);
      setCurrentGuess('');
      setAnimatingRow(-1);
      setLetterStates({});
    } catch (err) {
      setError('Failed to start game. Make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const submitGuess = useCallback(async () => {
    if (currentGuess.length !== 5) {
      setError('Guess must be exactly 5 letters');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/api/guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          game_id: gameId,
          guess: currentGuess,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Invalid guess');
      }

      const data = await response.json();
      
      // Set the row that should animate
      const currentRowIndex = humanGuesses.length;
      setAnimatingRow(currentRowIndex);
      
      // Add human guess
      const humanGuess = {
        word: currentGuess.toUpperCase(),
        result: data.human_result,
        guess_number: humanGuesses.length + 1
      };
      setHumanGuesses(prev => [...prev, humanGuess]);

      // Update letter states based on human guess
      updateLetterStates(humanGuess.word, humanGuess.result);

      // Add AI result colors (no letters yet)
      if (data.ai_result_colors && data.ai_result_colors.length > 0) {
        setAiResultsColors(prev => [...prev, {
          result: data.ai_result_colors,
          guess_number: data.ai_guess_number
        }]);
      }

      setGameComplete(data.game_complete);
      setCurrentGuess('');
      
      // Clear animation after it completes
      setTimeout(() => setAnimatingRow(-1), 900);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentGuess, gameId, humanGuesses.length, updateLetterStates]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameComplete || loading) return;

      const key = e.key.toUpperCase();
      
      if (key === 'ENTER') {
        e.preventDefault();
        if (currentGuess.length === 5) {
          submitGuess();
        }
      } else if (key === 'BACKSPACE') {
        e.preventDefault();
        setCurrentGuess(prev => prev.slice(0, -1));
        setError(''); // Clear any errors when user starts correcting
      } else if (key.match(/^[A-Z]$/) && currentGuess.length < 5) {
        e.preventDefault();
        setCurrentGuess(prev => prev + key);
        setError(''); // Clear any errors when user types
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameComplete, loading, currentGuess, submitGuess]);

  const revealGame = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/reveal/${gameId}`);
      const data = await response.json();
      
      setRevealedData(data);
      setGameRevealed(true);
    } catch (err) {
      setError('Failed to reveal game');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyboardClick = (key) => {
    if (gameComplete || loading) return;

    if (key === 'ENTER') {
      if (currentGuess.length === 5) {
        submitGuess();
      }
    } else if (key === 'BACKSPACE') {
      setCurrentGuess(prev => prev.slice(0, -1));
      setError(''); // Clear any errors when user starts correcting
    } else if (currentGuess.length < 5) {
      setCurrentGuess(prev => prev + key);
      setError(''); // Clear any errors when user types
    }
  };

  const getKeyState = (key) => {
    return letterStates[key] || '';
  };

  const renderGrid = (guesses, isAI = false, showLetters = true) => {
    const rows = [];
    
    for (let i = 0; i < 6; i++) {
      const guess = guesses[i];
      const row = [];
      
      for (let j = 0; j < 5; j++) {
        let cellClass = 'grid-cell';
        let letter = '';
        
        if (guess) {
          // This row has a completed guess
          if (showLetters) {
            letter = guess.word ? guess.word[j] : '';
          } else {
            letter = '?'; // Hide AI letters during game
          }
          
          const result = guess.result[j];
          cellClass += ` ${result}`;
          // Note: We show '?' for letters but still show the colors when !showLetters
        } else if (!isAI && i === guesses.length && currentGuess) {
          // This is the current active row for human player
          letter = currentGuess[j] || '';
          if (letter) {
            cellClass += ' filled';
          }
        }
        
        // Add animation classes for human guesses
        if (!isAI && i === animatingRow && guess) {
          cellClass += ` flipping flip-delay-${j}`;
        }
        
        row.push(
          <div key={j} className={cellClass}>
            {letter}
          </div>
        );
      }
      
      rows.push(
        <div key={i} className="grid-row">
          {row}
        </div>
      );
    }
    
    return <div className="game-grid">{rows}</div>;
  };

  const getDisplayGuesses = (isAI = false) => {
    if (isAI) {
      if (gameRevealed && revealedData) {
        return revealedData.ai_guesses;
      } else {
        // Show only colors, not letters
        return aiResultsColors.map(item => ({
          word: '?????', // Hidden letters
          result: item.result,
          guess_number: item.guess_number
        }));
      }
    }
    return humanGuesses;
  };

  const getGameStatus = () => {
    if (!gameComplete) return 'Game in progress...';
    
    if (revealedData) {
      const { human_won, ai_won, target_word, human_guesses, ai_guesses } = revealedData;
      
      // If both won, check who won in fewer guesses
      if (human_won && ai_won) {
        const humanGuessCount = human_guesses ? human_guesses.length : 0;
        const aiGuessCount = ai_guesses ? ai_guesses.length : 0;
        
        if (humanGuessCount < aiGuessCount) {
          return `You won! The word was: ${target_word}`;
        } else if (aiGuessCount < humanGuessCount) {
          return `AI won! The word was: ${target_word}`;
        } else {
          return `Both won! The word was: ${target_word}`;
        }
      }
      
      if (human_won) return `You won! The word was: ${target_word}`;
      if (ai_won) return `AI won! The word was: ${target_word}`;
      return `Nobody won! The word was: ${target_word}`;
    }
    
    return 'Game complete! Click "Reveal AI Guesses" to see how the AI played.';
  };

  // Determine actual winners considering tiebreakers (fewer guesses wins)
  const getWinners = () => {
    if (!revealedData) return { humanWinner: false, aiWinner: false };
    const { human_won, ai_won, human_guesses, ai_guesses } = revealedData;
    if (human_won && ai_won) {
      const h = human_guesses?.length || 0;
      const a = ai_guesses?.length || 0;
      if (h < a) return { humanWinner: true, aiWinner: false };
      if (a < h) return { humanWinner: false, aiWinner: true };
      return { humanWinner: true, aiWinner: true }; // exact tie
    }
    if (human_won) return { humanWinner: true, aiWinner: false };
    if (ai_won) return { humanWinner: false, aiWinner: true };
    return { humanWinner: false, aiWinner: false };
  };

  const getPlayerTitle = (isAI = false) => {
    const baseTitle = isAI ? 'AI' : 'YOU';
    
    if (gameComplete && revealedData) {
      const { humanWinner, aiWinner } = getWinners();
      if (isAI && aiWinner) {
        return `${baseTitle} ✓`;
      } else if (!isAI && humanWinner) {
        return `${baseTitle} ✓`;
      }
    }
    
    return baseTitle;
  };

  const getPlayerTitleClass = (isAI = false) => {
    let className = isAI ? 'player-title ai' : 'player-title human';
    
    if (gameComplete && revealedData) {
      const { humanWinner, aiWinner } = getWinners();
      if ((isAI && aiWinner) || (!isAI && humanWinner)) {
        className += ' winner';
      }
    }
    
    return className;
  };

  return (
    <div className="App">
      <div className="header">
        <h1>Is AI Good at Wordle?</h1>
        <p>Play Wordle alongside an AI and see who's better!</p>
      </div>

      {!gameId ? (
        <div>
          <button className="start-btn" onClick={startNewGame} disabled={loading}>
            {loading ? 'Starting...' : 'Start New Game'}
          </button>
        </div>
      ) : (
        <div>
          <div className={`game-container ${isMobile && gameComplete ? 'compact-bottom' : ''}`}>
            <div className="player-section">
              <div className={getPlayerTitleClass(false)}>{getPlayerTitle(false)}</div>
              {renderGrid(getDisplayGuesses(false), false, true)}
            </div>
            
            <div className="player-section">
              <div className={getPlayerTitleClass(true)}>{getPlayerTitle(true)}</div>
              {renderGrid(getDisplayGuesses(true), true, gameRevealed)}
            </div>
          </div>


          {gameComplete && !gameRevealed && (
            isMobile ? (
              <div className="reveal-overlay-mobile">
                <div className="reveal-card">
                  <h3>Game Complete!</h3>
                  <button className="reveal-btn" onClick={revealGame} disabled={loading}>
                    {loading ? 'Revealing...' : 'Reveal AI Guesses'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="game-complete">
                <h3>Game Complete!</h3>
                <button className="reveal-btn" onClick={revealGame} disabled={loading}>
                  {loading ? 'Revealing...' : 'Reveal AI Guesses'}
                </button>
              </div>
            )
          )}

          {gameRevealed && (
            <div className="game-complete">
              <h3>{getGameStatus()}</h3>
              <div className="stats">
                <div className={`stat ${getWinners().humanWinner ? 'winner' : 'loser'}`}>
                  <div className="stat-value">
                    {getWinners().humanWinner 
                      ? `✓ ${revealedData?.human_guesses?.length || 0}`
                      : `${revealedData?.human_guesses?.length || 0}+ ❌`
                    }
                  </div>
                  <div className="stat-label">Your Guesses</div>
                </div>
                <div className={`stat ${getWinners().aiWinner ? 'winner' : 'loser'}`}>
                  <div className="stat-value">
                    {getWinners().aiWinner 
                      ? `✓ ${revealedData?.ai_guesses?.length || 0}`
                      : `${revealedData?.ai_guesses?.length || 0}+ ❌`
                    }
                  </div>
                  <div className="stat-label">AI Guesses</div>
                </div>
              </div>
              <button className="start-btn" onClick={startNewGame}>
                Play Again
              </button>
            </div>
          )}

          <div className="game-status">
            <p>{getGameStatus()}</p>
            {!gameComplete && currentGuess.length === 5 && (
              <p style={{color: '#6aaa64', fontWeight: 'bold'}}>Press ENTER to submit your guess</p>
            )}
            {!gameComplete && currentGuess.length > 0 && currentGuess.length < 5 && (
              <p style={{color: '#818384'}}>Type {5 - currentGuess.length} more letter{5 - currentGuess.length !== 1 ? 's' : ''}</p>
            )}
          </div>

          {/* Scroll hint on mobile when results appear */}
          {isMobile && gameRevealed && showScrollHint && (
            <div className="scroll-hint">
              <span className="chevron">⌄</span>
              <span className="hint-text">Scroll for results</span>
            </div>
          )}

          {/* Virtual Keyboard */}
          <div className={`keyboard ${gameComplete && !gameRevealed && isMobile ? 'sticky-bottom-mobile' : ''}`}>
            {keyboardRows.map((row, rowIndex) => (
              <div key={rowIndex} className="keyboard-row">
                {row.map((key) => (
                  <button
                    key={key}
                    className={`keyboard-key ${key.length > 1 ? 'wide' : ''} ${getKeyState(key)}`}
                    onClick={() => handleKeyboardClick(key)}
                    disabled={loading}
                  >
                    {key === 'BACKSPACE' ? '⌫' : key}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default App;
